import { Dialog, DialogActions, DialogContent, DialogTitle, InputLabel } from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import AlertDialog from 'components/DialogComponent/AlertDialog';
import { formatDate } from 'helpers/DateFormatter';
import { APIEndpoints } from 'helpers/APILists';
import { MESSAGE_TYPE } from 'journeys/common/common.constants';
import TextInput from '../../elements/TextInput';
import { useStyles } from '../AdminForm.styled';
import { customSort, trackErrors } from '../helper';
import DropdownInput from '../../elements/DropdownInput';
import { AdminConstants } from 'journeys/portal/Admin/AdminScreen.constants';
import ModalFooterActions from '../../elements/ModalFooterActions';
import { DatePickerForForm } from 'elements/Inputs/DatePicker/DatepickerForForm';

const AlertNotificationForm = ({ isOpen, title, onClose, rowData, onRecordSubmit, bpRegionOptions }) => {
    const classes = useStyles();
    const [createRowData, setCreateRowData] = useState(rowData);
    const [cancelAlert, setCancelAlert] = useState(false);
    const { instance } = useMsal();
    const userName = instance.getActiveAccount();
    const submitButtonVisible = !_.isEqual(rowData, createRowData)
        && createRowData.BpRegion
        && createRowData.Title
        && createRowData.TextColor
        && createRowData.AlertStartDate
        && createRowData.AlertEndDate
        && createRowData.AlertMessage
        && createRowData.BackgroundColor
        && createRowData.Status;
    const gridStyle = {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '0 10px'
    };

    const regionAlias = useMemo(() => bpRegionOptions.find(
        x => x.BpRegion === createRowData.BpRegion
    )?.BpRegionAlias ?? '', [bpRegionOptions, createRowData.BpRegion]);

    useEffect(() => {
        setCreateRowData({ ...createRowData, BpRegionAlias: regionAlias });
        // eslint-disable-next-line
    }, [regionAlias]);

    const handleInputChange = ((e) => {
        setCreateRowData((prev) => (
            {
                ...prev,
                [e.target.name]: e.target.value
            }
        ));
    });

    const handleDateChange = (name, value) => {
        setCreateRowData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const cancelButtonHandler = () => {
        if (_.isEqual(rowData, createRowData)) {
            onClose();
        } else {
            setCancelAlert(true);
        }
    };

    const handleUnsaveChanges = () => {
        setCancelAlert(false);
    };

    const resetButtonHandler = () => {
        setCreateRowData(rowData);
    };

    const submitButtonHandler = () => {
        const payload = {
            ...createRowData,
            BpRegionAlias: regionAlias ?? '',
            Active: createRowData.Active === 'Active',
            LastModifiedBy: userName?.name,
            LastModifiedDate: formatDate(new Date()),
            AlertStartDate: formatDate(new Date(createRowData.AlertStartDate)),
            AlertEndDate: formatDate(new Date(createRowData.AlertEndDate))
        };

        if (createRowData.RowId) {
            updateRecord(payload);
        } else {
            payload.RowId = 0;
            payload.CreatedBy = userName?.name;
            payload.CreatedDate = formatDate(new Date());
            submitRecord(payload);
        }
    };

    const submitRecord = (dataRow) => {
        axios.post(APIEndpoints.ADMIN_AddAlertNotification, dataRow)
            .then((res) => {
                onRecordSubmit(res.data.RowId);
                onClose();
            })
            .catch((err) => {
                trackErrors({
                    messageType: MESSAGE_TYPE.ERROR,
                    message: err.message,
                    exception: err
                });
            });
    };

    const updateRecord = (dataRow) => {
        axios.put(APIEndpoints.ADMIN_UpdateAlertNotification, dataRow)
            .then((res) => {
                onRecordSubmit(res.data.RowId);
                onClose();
            })
            .catch((err) => {
                trackErrors({
                    messageType: MESSAGE_TYPE.ERROR,
                    message: err.message,
                    exception: err
                });
            });
    };

    return (
        <Dialog
            open={isOpen}
            scroll="paper"
            maxWidth="lg"
        >
            <DialogTitle id="scroll-dialog-title" classes={{ root: classes.headerRoot }} className={classes.header}>{title}</DialogTitle>
            {cancelAlert && (
                <AlertDialog
                    dialogMessage={AdminConstants.DIALOG_MESSAGE}
                    dialogOpenState={cancelAlert}
                    handleClose={() => handleUnsaveChanges()}
                    handleAlertFunction={onClose}
                />
            )}
            <DialogContent>
                <div style={gridStyle}>
                    <DropdownInput
                        name="BpRegion"
                        title="BP Region (SAP PM)"
                        value={createRowData.BpRegion}
                        required
                        handleInputChange={handleInputChange}
                        options={['ALL', ...customSort(_.uniq(bpRegionOptions.map(x => x.BpRegion).filter(region => region !== 'ALL')))]}
                    />
                    <TextInput
                        name="BpRegionAlias"
                        title="BP Region Alias"
                        value={createRowData?.BpRegionAlias}
                        disabled
                        handleInputChange={handleInputChange}
                        type="string"
                    />
                    <TextInput
                        name="Title"
                        title="Title"
                        value={createRowData?.Title}
                        required
                        handleInputChange={handleInputChange}
                        type="string"
                        maxLength="30"
                        isMultiline
                    />
                    <div style={{ position: 'relative' }}>
                        <DropdownInput
                            name="TextColor"
                            title="Text Color"
                            value={createRowData.TextColor}
                            required
                            handleInputChange={handleInputChange}
                            options={[{ label: 'Red', value: 'Red' }, { label: 'Blue', value: 'Blue' }, { label: 'Black', value: 'Black' }, { label: 'Orange', value: 'Orange' }].sort((a, b) => a.label.localeCompare(b.label))}
                        />
                        <span style={{ width: '40px', height: '23px', display: 'inline-block', position: 'absolute', top: '33%', right: '15%', backgroundColor: createRowData?.TextColor }} />
                    </div>
                </div>
                <div style={{ display: 'grid', marginTop: '1px', marginBottom: '10px' }}>
                    <TextInput
                        name="AlertMessage"
                        title="Alert Message"
                        value={createRowData.AlertMessage}
                        required
                        handleInputChange={handleInputChange}
                        isMultiline
                        width="1080px"
                        type="string"
                        maxLength="1500"
                    />
                </div>
                <div style={gridStyle}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <InputLabel style={{ fontSize: '12px', color: 'black', paddingLeft: '3px', paddingBottom: '0', fontWeight: '600' }}>
                            Alert Start Date
                        </InputLabel>
                        <DatePickerForForm
                            name="AlertStartDate"
                            title="Alert Start Date"
                            value={createRowData.AlertStartDate || null}
                            isRequired
                            onChange={(date) => handleDateChange('AlertStartDate', date)}
                            textAlign="center"
                            width="95%"
                            fontSize="11px"
                            height="27px"
                            borderStyle="none"
                            borderRadius="3px"
                            marginTop="0px"
                        />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <InputLabel style={{ fontSize: '12px', color: 'black', paddingLeft: '3px', paddingBottom: '0', fontWeight: '600' }}>
                            Alert End Date
                        </InputLabel>
                        <DatePickerForForm
                            name="AlertEndDate"
                            title="Alert End Date"
                            value={createRowData.AlertEndDate || null}
                            isRequired
                            onChange={(date) => handleDateChange('AlertEndDate', date)}
                            textAlign="center"
                            width="95%"
                            fontSize="11px"
                            height="27px"
                            borderStyle="none"
                            borderRadius="3px"
                            marginTop="0px"
                        />
                    </div>
                    <div style={{ position: 'relative' }}>
                        <DropdownInput
                            name="BackgroundColor"
                            title="Background Color"
                            value={createRowData.BackgroundColor}
                            required
                            handleInputChange={handleInputChange}
                            options={[{ label: 'Green', value: 'Green' }, { label: 'Blue', value: 'Blue' }, { label: 'White', value: 'White' }, { label: 'Orange', value: 'Orange' }].sort((a, b) => a.label.localeCompare(b.label))}
                        />
                        <span style={{ width: '40px', height: '23px', display: 'inline-block', position: 'absolute', top: '33%', right: '15%', backgroundColor: createRowData?.BackgroundColor }} />
                    </div>
                    <DropdownInput
                        name="Status"
                        title="Alert Status"
                        value={createRowData.Status}
                        handleInputChange={handleInputChange}
                        options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                        required
                    />

                </div>
                <div style={gridStyle}>
                    <TextInput
                        name="CreatedBy"
                        title="Created By"
                        value={createRowData?.RowId ? createRowData.CreatedBy : userName?.name}
                        disabled
                        handleInputChange={handleInputChange}
                    />
                    <TextInput
                        name="CreatedDate"
                        title="Created Date"
                        value={createRowData?.RowId ? formatDate(new Date(createRowData.CreatedDate)) : formatDate(new Date())}
                        disabled
                        handleInputChange={handleInputChange}
                    />
                    <TextInput
                        name="LastModifiedBy"
                        title="Last Modified By"
                        value={createRowData.LastModifiedBy || userName?.name}
                        disabled
                        handleInputChange={handleInputChange}
                    />
                    <TextInput
                        name="LastModifiedDate"
                        title="Last Modified Date"
                        value={createRowData?.RowId ? formatDate(new Date(createRowData.LastModifiedDate)) : formatDate(new Date())}
                        disabled
                        handleInputChange={handleInputChange}
                    />
                </div>
                <div style={{ display: 'grid', marginTop: '12px' }}>
                    <TextInput
                        name="Comment"
                        title="Comment"
                        value={createRowData.Comment}
                        handleInputChange={handleInputChange}
                        isMultiline
                        width="1080px"
                    />
                </div>
            </DialogContent>
            <DialogActions style={{ marginRight: '15px', paddingBottom: '18px', paddingTop: '15px', justifyContent: 'space-between' }}>
                <div>
                    <InputLabel className={classes.inputLabel}>
                        <i> Note: If BP Region is not available in the list please add a new record in Group Mapping table. </i>
                    </InputLabel>
                    <InputLabel className={classes.inputLabelWithPadding}>
                        <i>Alert Notification feature. This feature is used to inform users of any events that may affect the performance or outages.</i>
                    </InputLabel>
                </div>
                <ModalFooterActions
                    submitButtonVisible={submitButtonVisible}
                    submitButtonHandler={submitButtonHandler}
                    resetButtonHandler={resetButtonHandler}
                    cancelButtonHandler={cancelButtonHandler}
                />
            </DialogActions>
        </Dialog>

    );
};

export default AlertNotificationForm;

AlertNotificationForm.propTypes = {
    bpRegionOptions: PropTypes.array,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onRecordSubmit: PropTypes.func,
    rowData: PropTypes.object,
    title: PropTypes.string
};
